import { CsvEnrichmentStatistics } from "@signalist/shared-types";

import { InclusionFilterStatus } from "@/enum/list.enum";

import { toCount } from "../../utils/exclusionReasonMeta";
import {
	buildGroupedShowReasons,
	GroupedShowReason,
} from "../UnassignedTable/utils/getGroupedShowOptions";

/** One statistics row: a label, a number and — for a lead bucket — its reason breakdown. */
export interface StatisticsModalRow {
	key: string;
	label: string;
	tone: "neutral" | "ok" | "warn" | "bad";
	value: number;
	reasons?: GroupedShowReason[];
}

/** One titled section of the modal. */
export interface StatisticsModalGroup {
	key: string;
	title: string;
	rows: StatisticsModalRow[];
}

type Translate = (key: string) => string;

/**
 * PREC-3870 — the after-enrichment XLS Statistics modal as data: which sections it has and what
 * is in them, given one source's statistics payload. Pure, so the grouping rules are testable
 * without rendering anything.
 *
 * The lead buckets and their reason children come from `buildGroupedShowReasons`, the same builder
 * the list's grouped Show dropdown uses — the modal must break a bucket down identically or the
 * two read as different answers to the same question.
 *
 * `ts` resolves under `csvEnrichment.statsModal`; `tsInbox` resolves the shared bucket and reason
 * labels under `inbox` (where `enListRecovery` is spread), so the modal never invents its own
 * wording for a bucket the list already names.
 */
export const buildStatisticsModalGroups = (
	ts: Translate,
	tsInbox: Translate,
	statistics?: CsvEnrichmentStatistics
): StatisticsModalGroup[] => {
	if (!statistics) {
		return [];
	}

	return [
		importGroup(ts, statistics),
		...screeningGroup(ts, statistics),
		...companiesGroup(ts, statistics),
		enrichmentGroup(ts, tsInbox, statistics),
	];
};

const importGroup = (ts: Translate, statistics: CsvEnrichmentStatistics): StatisticsModalGroup => ({
	key: "import",
	title: ts("groupImport"),
	rows: [
		{
			key: "rowsDetected",
			label: ts("rowsDetected"),
			tone: "neutral",
			value: toCount(statistics.import.rowsDetected),
		},
		{
			key: "rowsReady",
			label: ts("rowsReady"),
			tone: "ok",
			value: toCount(statistics.import.rowsReady),
		},
		{
			key: "notImported",
			label: ts("notImported"),
			tone: "warn",
			value: toCount(statistics.import.notImported),
		},
	],
});

/**
 * People uploads only, and only when the file actually carried titles. Note the labels say
 * "Job titles whitelisted", not "New job titles whitelisted" — see the API-side note on
 * `buildScreeningStats`: which titles were new when the file ran is not recoverable.
 */
const screeningGroup = (
	ts: Translate,
	statistics: CsvEnrichmentStatistics
): StatisticsModalGroup[] =>
	statistics.screening
		? [
				{
					key: "screening",
					title: ts("groupScreening"),
					rows: [
						{
							key: "whitelisted",
							label: ts("whitelisted"),
							tone: "ok",
							value: toCount(statistics.screening.whitelisted),
						},
						{
							key: "blacklisted",
							label: ts("blacklisted"),
							tone: "bad",
							value: toCount(statistics.screening.blacklisted),
						},
					],
				},
			]
		: [];

/**
 * Company uploads only: the per-input-row outcomes the scrapers stamp. `mergedInto` is shown
 * rather than hidden because it is the answer to "why did 412 rows produce 398 companies" —
 * accounted for, not lost (docs/company-import-loss-analysis.md).
 */
const companiesGroup = (
	ts: Translate,
	statistics: CsvEnrichmentStatistics
): StatisticsModalGroup[] => {
	const companies = statistics.companies;

	if (!companies) {
		return [];
	}

	const rows: StatisticsModalRow[] = [
		{
			key: "companiesFound",
			label: ts("companiesFound"),
			tone: "ok",
			value: toCount(companies.found),
		},
		{
			key: "companiesMerged",
			label: ts("companiesMerged"),
			tone: "neutral",
			value: toCount(companies.mergedInto),
		},
		{
			key: "companiesNotFound",
			label: ts("companiesNotFound"),
			tone: "bad",
			value: toCount(companies.notFound),
		},
		{
			key: "companiesExcluded",
			label: ts("companiesExcluded"),
			tone: "warn",
			value: toCount(companies.excluded),
		},
	];

	// Pending only exists while a run is still going; showing a zero would imply we were waiting.
	return [
		{
			key: "companies",
			title: ts("groupCompanies"),
			rows: toCount(companies.pending)
				? [
						...rows,
						{
							key: "companiesPending",
							label: ts("companiesPending"),
							tone: "neutral",
							value: toCount(companies.pending),
						},
					]
				: rows,
		},
	];
};

const enrichmentGroup = (
	ts: Translate,
	tsInbox: Translate,
	statistics: CsvEnrichmentStatistics
): StatisticsModalGroup => {
	const { enrichment } = statistics;
	const reasonsFor = (status: string) =>
		buildGroupedShowReasons(tsInbox, status, enrichment.exclusionReasonCounts);

	const rows: StatisticsModalRow[] = [
		{
			key: "found",
			label: tsInbox("filterOptions.ADDED"),
			tone: "ok",
			value: toCount(enrichment.found),
			reasons: reasonsFor(InclusionFilterStatus.ADDED),
		},
		{
			key: "unsure",
			label: tsInbox("filterOptions.UNSURE"),
			tone: "warn",
			value: toCount(enrichment.unsure),
			reasons: reasonsFor(InclusionFilterStatus.UNSURE),
		},
		{
			key: "notFound",
			label: tsInbox("filterOptions.NOT_FOUND"),
			tone: "bad",
			value: toCount(enrichment.notFound),
			reasons: reasonsFor(InclusionFilterStatus.NOT_FOUND),
		},
	];

	// Blacklist is only worth a row when the file actually produced one — it is not part of the
	// found/unsure/not-found partition the user is reading, just a tail the list also tracks.
	if (toCount(enrichment.blacklist)) {
		rows.push({
			key: "blacklist",
			label: tsInbox("filterOptions.BLACKLIST"),
			tone: "bad",
			value: toCount(enrichment.blacklist),
			reasons: reasonsFor(InclusionFilterStatus.BLACKLIST),
		});
	}

	return { key: "enrichment", title: ts("groupEnrichment"), rows };
};
